import "./TabelaExtratoAdmin.css";
import { FaRegEdit, FaTrashAlt, FaUndoAlt } from "react-icons/fa";
import { useState, useMemo, useEffect } from "react";
import Modal from "./Modal";
import FormularioComissao from "../../Formulario/FormularioComissao";
import FormularioPedidoOmie from "../../Formulario/FormularioPedidoOmie";
import Api from "../../Services/Api";

const CANCEL_STATUS_VALUES = new Set([
  "INACTIVE",
  "INATIVO",
  "DELETED",
  "EXCLUIDO",
  "EXCLUDED",
  "REMOVED",
]);

const STATUS_ORDER = [
  "SEM_STATUS",
  "PENDING",
  "AWAITING_CONFIRMATION",
  "CONFIRMED",
  "WITHDRAW_REQUESTED",
  "CANCELED",
];
const DATE_PREFIX_REGEX = /^(\d{4})-(\d{2})-(\d{2})(?:$|[T\s])/;
const CLIENT_NAME_MAX_LENGTH = 48;

function toFiniteNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function pickFirst(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return null;
}

function formatClientDisplayName(value, maxLength = CLIENT_NAME_MAX_LENGTH) {
  const text = String(value || "").trim();
  if (!text) return "-";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trimEnd()}...`;
}

function parseJson(raw) {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string") return null;
  try {
    return JSON.parse(raw);
  } catch (_err) {
    return null;
  }
}

function extractRawOrder(rawInput) {
  const parsed = parseJson(rawInput);
  if (!parsed) return null;
  if (parsed.pedido_venda_produto) return parsed.pedido_venda_produto;
  if (parsed.PedidoVendaProduto) return parsed.PedidoVendaProduto;
  return parsed;
}

function extractInstallmentsCount(rawInput) {
  const raw = extractRawOrder(rawInput);
  if (!raw || typeof raw !== "object") return 1;

  const cabecalho = raw.cabecalho || raw.Cabecalho || {};
  const blocoParcelas = raw.lista_parcelas || raw.ListaParcelas || raw.parcelas || raw.Parcelas || {};
  const parcelasRaw = pickFirst(
    blocoParcelas.parcela,
    blocoParcelas.Parcela,
    raw.parcela,
    raw.parcelas
  );

  const qtdParcelasLista = Array.isArray(parcelasRaw) ? parcelasRaw.filter(Boolean).length : parcelasRaw ? 1 : 0;
  const qtdParcelasCabecalho = Number(toFiniteNumber(cabecalho?.qtde_parcelas) || 0);

  return Math.max(1, qtdParcelasLista, qtdParcelasCabecalho);
}

const TabelaExtratoAdmin = (props) => {
  const licenciados = props.licenciados || [];

  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [confirmacao, setConfirmacao] = useState(null);
  const [processandoAcao, setProcessandoAcao] = useState(false);
  const [processandoSeparacaoKey, setProcessandoSeparacaoKey] = useState("");
  const [busca, setBusca] = useState("");
  const [statusFiltro, setStatusFiltro] = useState("ALL");

  const ITENS_POR_PAGINA = 20;
  const itens = useMemo(() => props.itensLista || [], [props.itensLista]);

  const normalizarTexto = (value) =>
    String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toUpperCase()
      .trim();

  const statusNormalizado = (value) => normalizarTexto(value);

  const formatarStatusLivre = (status) =>
    String(status || "")
      .toLowerCase()
      .split("_")
      .map((parte) => (parte ? parte.charAt(0).toUpperCase() + parte.slice(1) : ""))
      .join(" ");

  const isStatusCancelado = (statusInput) => {
    const status = statusNormalizado(statusInput);
    if (!status) return false;
    if (status.includes("CANCEL")) return true;
    return CANCEL_STATUS_VALUES.has(status);
  };

  const isItemCancelado = (item) => isStatusCancelado(item?.status);

  const nomeLicenciado = (licenciado_id) => {
    const l = licenciados.find((x) => String(x.id) === String(licenciado_id));
    return l?.name || (licenciado_id ? `#${licenciado_id}` : "-");
  };

  const obterTipoInfo = (item) => {
    const isOmie = item?.source === "OMIE";
    const isLedger = item?.source === "LEDGER";
    const isSaque = isLedger && item?.origin === "WITHDRAW";

    let tipoLabel;
    if (isOmie) {
      const origem = item?.origem_pedido;
      tipoLabel = origem ? `Pedido Omie - ${origem}` : "Pedido Omie";
    } else {
      tipoLabel = isSaque ? "Debito" : "Credito";
    }

    const tipoClass = isOmie ? "tipo-omie" : isSaque ? "tipo-debito" : "tipo-credito";

    return { isOmie, isLedger, isSaque, tipoLabel, tipoClass };
  };

  const obterChaveStatus = (item) => {
    if (isItemCancelado(item)) return "CANCELED";
    const status = statusNormalizado(item?.status);
    return status || "SEM_STATUS";
  };

  const obterLabelStatus = (status, item) => {
    if (status === "SEM_STATUS") return "-";
    if (status === "CANCELED") return "Cancelado";
    if (status === "PENDING") return "Pendente";
    if (status === "AWAITING_CONFIRMATION") return "Aguardando confirmacao";
    if (status === "WITHDRAW_REQUESTED") return "Saque solicitado";
    if (status === "CONFIRMED") {
      const isSaqueConfirmado =
        item?.source === "LEDGER" &&
        item?.origin === "WITHDRAW" &&
        statusNormalizado(item?.status) === "CONFIRMED";
      return isSaqueConfirmado ? "Credito Efetuado" : "Confirmado";
    }
    return formatarStatusLivre(status);
  };

  const opcoesStatus = (() => {
    const unicos = new Set(itens.map((item) => obterChaveStatus(item)));
    const ordenados = Array.from(unicos).sort((a, b) => {
      const idxA = STATUS_ORDER.indexOf(a);
      const idxB = STATUS_ORDER.indexOf(b);
      if (idxA === -1 && idxB === -1) return a.localeCompare(b);
      if (idxA === -1) return 1;
      if (idxB === -1) return -1;
      return idxA - idxB;
    });

    return [
      { value: "ALL", label: "Todos os status" },
      ...ordenados.map((status) => ({
        value: status,
        label: obterLabelStatus(status),
      })),
    ];
  })();

  const itensFiltrados = (() => {
    const termoBusca = normalizarTexto(busca);

    return itens.filter((item) => {
      const chaveStatus = obterChaveStatus(item);
      if (statusFiltro !== "ALL" && chaveStatus !== statusFiltro) return false;
      if (!termoBusca) return true;

      const { tipoLabel } = obterTipoInfo(item);
      const textoBusca = [
        formatarDataBRL(item?.data),
        nomeLicenciado(item?.licenciado_id),
        item?.numero,
        item?.cliente,
        tipoLabel,
        obterLabelStatus(chaveStatus, item),
        item?.status,
      ]
        .filter(Boolean)
        .join(" ");

      return normalizarTexto(textoBusca).includes(termoBusca);
    });
  })();

  const totalPaginas = Math.max(1, Math.ceil(itensFiltrados.length / ITENS_POR_PAGINA));

  const itensPaginados = useMemo(() => {
    const inicio = (paginaAtual - 1) * ITENS_POR_PAGINA;
    const fim = inicio + ITENS_POR_PAGINA;
    return itensFiltrados.slice(inicio, fim);
  }, [itensFiltrados, paginaAtual]);

  useEffect(() => {
    if (paginaAtual > totalPaginas) {
      setPaginaAtual(totalPaginas);
    }
  }, [paginaAtual, totalPaginas]);

  useEffect(() => {
    setPaginaAtual(1);
  }, [busca, statusFiltro]);

  const abrirEdicao = (item) => {
    setEditItem(item);
    setOpen(true);
  };

  const fechar = () => {
    setOpen(false);
    setEditItem(null);
  };

  const obterStatusReativacao = (item) => {
    if (item?.source === "LEDGER") {
      if (item?.origin === "WITHDRAW") return "WITHDRAW_REQUESTED";
      return "PENDING";
    }

    return "PENDING";
  };

  const abrirConfirmacaoStatus = (item) => {
    const cancelado = isItemCancelado(item);

    setConfirmacao({
      item,
      acao: cancelado ? "reativar" : "excluir",
      proximoStatus: cancelado ? obterStatusReativacao(item) : "CANCELED",
    });
  };

  const fecharConfirmacaoStatus = () => {
    if (processandoAcao) return;
    setConfirmacao(null);
  };

  const confirmarTrocaStatus = async () => {
    if (!confirmacao?.item) return;

    try {
      setProcessandoAcao(true);

      if (confirmacao.item.source === "LEDGER") {
        await Api.patch(`/ledger-entries/${confirmacao.item.id}/status`, {
          status: confirmacao.proximoStatus,
        });
      } else {
        await Api.patch(`/omie/orders/${confirmacao.item.id}`, {
          status: confirmacao.proximoStatus,
        });
      }

      props.onItemAtualizado?.({
        id: confirmacao.item.id,
        source: confirmacao.item.source,
        status: confirmacao.proximoStatus,
      });

      await props.onReload?.();
      setConfirmacao(null);
    } catch (err) {
      alert(err?.response?.data?.message || "Nao foi possivel atualizar o status do lancamento.");
    } finally {
      setProcessandoAcao(false);
    }
  };

  const separarParcelasManualmente = async (item, installmentsCount) => {
    if (!item?.id) return;

    const confirmar = window.confirm(`Separar este lancamento em ${installmentsCount}x?`);
    if (!confirmar) return;

    const itemKey = item.uniqueKey || `${item.source}_${item.id}`;

    try {
      setProcessandoSeparacaoKey(itemKey);

      const { data } = await Api.post(`/ledger-entries/${item.id}/split-installments`);
      const parcelasGeradas = Number(data?.installments_count || data?.ledger_entries?.length || 0);

      alert(
        parcelasGeradas > 0
          ? `Parcelas geradas com sucesso (${parcelasGeradas}).`
          : "Parcelas geradas com sucesso."
      );

      await props.onReload?.();
    } catch (err) {
      alert(err?.response?.data?.message || "Nao foi possivel separar as parcelas.");
    } finally {
      setProcessandoSeparacaoKey("");
    }
  };

  function formatCurrencyBRL(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return "R$ 0,00";
    return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  }

  function formatarDataBRL(data) {
    if (!data) return "-";
    const text = String(data).trim();
    const dateOnlyMatch = text.match(DATE_PREFIX_REGEX);
    if (dateOnlyMatch) {
      return `${dateOnlyMatch[3]}/${dateOnlyMatch[2]}/${dateOnlyMatch[1]}`;
    }

    const parsed = new Date(text);
    if (Number.isNaN(parsed.getTime())) return "-";

    return new Intl.DateTimeFormat("pt-BR", {
      timeZone: "America/Sao_Paulo",
    }).format(parsed);
  }

  function extrairParcelaDaDescricao(descricao) {
    const match = String(descricao || "").match(/PARCELA\s+(\d+)\s*\/\s*(\d+)/i);
    if (!match) return "";
    return `Parcela ${match[1]}/${match[2]}`;
  }

  function formatarNumeroCompra(item) {
    const numero = String(item?.numero || "").trim();
    const parcelaInfo = extrairParcelaDaDescricao(item?.descricao);
    if (!numero && !parcelaInfo) return "-";
    if (!parcelaInfo) return numero || "-";
    if (!numero) return parcelaInfo;
    return `${numero} (${parcelaInfo})`;
  }

  function renderizarStatus(item) {
    const chaveStatus = obterChaveStatus(item);

    switch (chaveStatus) {
      case "CONFIRMED":
        return (
          <span className="status-pill status-credito">
            {obterLabelStatus(chaveStatus, item)}
          </span>
        );
      case "PENDING":
        return (
          <span className="status-pill status-analise">
            {obterLabelStatus(chaveStatus, item)}
          </span>
        );
      case "AWAITING_CONFIRMATION":
        return (
          <span className="status-pill status-aguardando-confirmacao">
            {obterLabelStatus(chaveStatus, item)}
          </span>
        );
      case "WITHDRAW_REQUESTED":
        return (
          <span className="status-pill status-saque-solicitado">
            {obterLabelStatus(chaveStatus, item)}
          </span>
        );
      case "CANCELED":
        return <span className="status-pill">Cancelado</span>;
      case "SEM_STATUS":
        return <span className="status-pill">-</span>;
      default:
        return <span className="status-pill">{obterLabelStatus(chaveStatus, item)}</span>;
    }
  }

  const inicioPaginacao =
    itensFiltrados.length === 0 ? 0 : (paginaAtual - 1) * ITENS_POR_PAGINA + 1;
  const fimPaginacao = Math.min(paginaAtual * ITENS_POR_PAGINA, itensFiltrados.length);

  return (
    <section className="table-card">
      <div className="table-title">{props.children}</div>

      <div className="table-toolbar">
        <input
          type="text"
          className="table-search-input"
          placeholder="Filtrar por pedido, cliente, licenciado, tipo ou status"
          value={busca}
          onChange={(event) => setBusca(event.target.value)}
        />

        <select
          className="table-status-select"
          value={statusFiltro}
          onChange={(event) => setStatusFiltro(event.target.value)}
        >
          {opcoesStatus.map((opcao) => (
            <option key={opcao.value} value={opcao.value}>
              {opcao.label}
            </option>
          ))}
        </select>
      </div>

      <div className="table-tr">
        <table>
          <thead>
            <tr>
              <th>Data</th>
              <th>Licenciado</th>
              <th>N compra</th>
              <th>Cliente</th>
              <th>Valor compra</th>
              <th>Valor comissao</th>
              <th>Tipo</th>
              <th>Status</th>
              <th>Acoes</th>
            </tr>
          </thead>

          <tbody id="extratoTableBody">
            {itensPaginados.length === 0 && (
              <tr>
                <td colSpan={9} style={{ textAlign: "center" }}>
                  Nenhum lancamento encontrado para os filtros aplicados
                </td>
              </tr>
            )}

            {itensPaginados.map((item) => {
              const { isOmie, isLedger, isSaque, tipoLabel, tipoClass } = obterTipoInfo(item);
              const isCancelado = isItemCancelado(item);
              const clienteCompleto = String(item?.cliente || "").trim() || "-";
              const clienteExibido = formatClientDisplayName(clienteCompleto);
              const valorCompra = Number(item.valor_compra) || 0;
              const installmentsCount = extractInstallmentsCount(item?.raw_json);
              const itemJaSeparado = Boolean(extrairParcelaDaDescricao(item?.descricao));
              const podeSepararNaLinha =
                isLedger &&
                !isSaque &&
                !isCancelado &&
                !itemJaSeparado &&
                installmentsCount > 1;
              const itemKey = item.uniqueKey || `${item.source}_${item.id}`;
              const separandoNaLinha = processandoSeparacaoKey === itemKey;

              const pct = Number(item.percentual) || 0;
              const comissaoCalc = pct > 0 ? (valorCompra * pct) / 100 : 0;
              const comissao = isOmie
                ? Number(item.valor_comissao) || comissaoCalc || 0
                : Number(item.valor_comissao) || 0;

              return (
                <tr
                  key={item.uniqueKey || `${item.source}_${item.id}`}
                  className={isCancelado ? "row-cancelled" : ""}
                >
                  <td>{formatarDataBRL(item.data)}</td>
                  <td>{nomeLicenciado(item.licenciado_id)}</td>
                  <td>{formatarNumeroCompra(item)}</td>
                  <td className="cliente-cell" title={clienteCompleto}>
                    <span className="cliente-text">{clienteExibido}</span>
                  </td>
                  <td>{formatCurrencyBRL(valorCompra)}</td>
                  <td>{formatCurrencyBRL(isSaque ? -Math.abs(valorCompra) : comissao)}</td>

                  <td className={tipoClass}>{tipoLabel}</td>
                  <td>{renderizarStatus(item)}</td>

                  <td className="td-actions">
                    <div className="actions-wrapper">
                      {podeSepararNaLinha && (
                        <button
                          type="button"
                          className="action-btn action-btn-split"
                          title={`Separar em ${installmentsCount}x`}
                          onClick={() => separarParcelasManualmente(item, installmentsCount)}
                          disabled={separandoNaLinha}
                        >
                          {separandoNaLinha ? "Separando..." : "Separar"}
                        </button>
                      )}

                      <button
                        type="button"
                        className="icon-btn"
                        title={
                          isCancelado
                            ? "Reative o lancamento para editar"
                            : isOmie
                              ? "Vincular/confirmar pedido"
                              : "Editar lancamento"
                        }
                        onClick={() => abrirEdicao(item)}
                        disabled={isCancelado}
                      >
                        <FaRegEdit />
                      </button>

                      <button
                        type="button"
                        className={`icon-btn ${isCancelado ? "icon-btn-success" : "icon-btn-danger"}`}
                        title={isCancelado ? "Reativar lancamento" : "Excluir lancamento"}
                        onClick={() => abrirConfirmacaoStatus(item)}
                      >
                        {isCancelado ? <FaUndoAlt /> : <FaTrashAlt />}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {itensFiltrados.length > ITENS_POR_PAGINA && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px",
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <div style={{ fontSize: 14, color: "#666" }}>
            Mostrando {inicioPaginacao} a {fimPaginacao} de {itensFiltrados.length} itens
            {itensFiltrados.length !== itens.length ? ` (filtrados de ${itens.length})` : ""}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              type="button"
              onClick={() => setPaginaAtual((p) => Math.max(1, p - 1))}
              disabled={paginaAtual === 1}
              style={{
                padding: "6px 12px",
                border: "1px solid #ddd",
                borderRadius: 4,
                background: paginaAtual === 1 ? "#f5f5f5" : "#fff",
                cursor: paginaAtual === 1 ? "not-allowed" : "pointer",
                opacity: paginaAtual === 1 ? 0.5 : 1,
              }}
            >
              Anterior
            </button>
            <span style={{ fontSize: 14, color: "#666" }}>
              Pagina {paginaAtual} de {totalPaginas}
            </span>
            <button
              type="button"
              onClick={() => setPaginaAtual((p) => Math.min(totalPaginas, p + 1))}
              disabled={paginaAtual === totalPaginas}
              style={{
                padding: "6px 12px",
                border: "1px solid #ddd",
                borderRadius: 4,
                background: paginaAtual === totalPaginas ? "#f5f5f5" : "#fff",
                cursor: paginaAtual === totalPaginas ? "not-allowed" : "pointer",
                opacity: paginaAtual === totalPaginas ? 0.5 : 1,
              }}
            >
              Proxima
            </button>
          </div>
        </div>
      )}

      <Modal
        open={open}
        title={editItem?.source === "OMIE" ? "Pedido Omie" : "Editar lancamento"}
        onClose={fechar}
      >
        {editItem?.source === "LEDGER" && (
          <FormularioComissao
            licenciados={licenciados}
            modo="edit"
            editId={editItem?.id}
            itemInicial={editItem}
            onClose={fechar}
            lancamentoAtualizado={(itemAtualizado) => {
              props.onItemAtualizado?.(itemAtualizado);
              props.onReload?.();
              fechar();
            }}
          />
        )}

        {editItem?.source === "OMIE" && (
          <FormularioPedidoOmie
            licenciados={licenciados}
            pedido={editItem}
            onClose={fechar}
            onAtualizado={(patch) => {
              props.onItemAtualizado?.({ id: editItem.id, source: editItem.source, ...patch });
            }}
            onReload={props.onReload}
          />
        )}
      </Modal>

      <Modal
        open={Boolean(confirmacao)}
        title={confirmacao?.acao === "excluir" ? "Confirmar exclusao" : "Confirmar reativacao"}
        onClose={fecharConfirmacaoStatus}
      >
        <div className="confirm-status-content">
          {confirmacao?.acao === "excluir" ? (
            <p>
              O lancamento sera desativado e ficara riscado na tabela. Tudo que estiver relacionado
              tambem sera inativado.
            </p>
          ) : (
            <p>
              O lancamento sera reativado. Registros relacionados tambem voltarao a ficar ativos.
            </p>
          )}

          <div className="confirm-status-actions">
            <button
              type="button"
              className="action-btn"
              onClick={fecharConfirmacaoStatus}
              disabled={processandoAcao}
            >
              Cancelar
            </button>
            <button
              type="button"
              className={`action-btn ${confirmacao?.acao === "excluir" ? "action-btn-danger" : "action-btn-success"}`}
              onClick={confirmarTrocaStatus}
              disabled={processandoAcao}
            >
              {processandoAcao
                ? "Processando..."
                : confirmacao?.acao === "excluir"
                  ? "Confirmar exclusao"
                  : "Confirmar reativacao"}
            </button>
          </div>
        </div>
      </Modal>
    </section>
  );
};

export default TabelaExtratoAdmin;
